import { useCallback, useEffect, useRef, useState } from 'react';
import { buildNode } from '../utils/buildNode';
import { layoutNodes } from '../utils/layoutEngine';
import { SCENE_TOP_PAD } from '../utils/constants';

/**
 * Measures the editor DOM into a mini-space scene (render items + doc↔mini
 * anchors) and keeps it in sync via ResizeObserver / MutationObserver.
 * Scroll position is tracked separately (cheap — no Range walks).
 */
export const useMinimapScene = (editorRootRef, scrollEl, setScrollEl, dragRef) => {
  const [rect, setRect] = useState(null);
  const [scene, setScene] = useState({ items: [], anchors: [], totalH: 0 });
  const [metrics, setMetrics] = useState({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 });
  const deferredRef = useRef(false);

  const syncRect = useCallback(() => {
    if (!scrollEl?.isConnected) return;
    const r = scrollEl.getBoundingClientRect();
    if (r.height <= 0) return;
    setRect((prev) => {
      if (prev && prev.top === r.top && prev.right === r.right && prev.height === r.height) {
        return prev;
      }
      return { top: r.top, right: r.right, height: r.height };
    });
  }, [scrollEl]);

  const measure = useCallback(() => {
    const root = editorRootRef.current;
    if (!root || !scrollEl) return;
    if (!scrollEl.isConnected) {
      setScrollEl(null);
      setRect(null);
      return;
    }
    const r = scrollEl.getBoundingClientRect();
    const blocksEl = root.querySelector('.doc-editor-blocks');
    const blocksRect = blocksEl?.getBoundingClientRect();
    if (!blocksRect || blocksRect.width <= 0) return;

    setRect({ top: r.top, right: r.right, height: r.height });
    setMetrics({
      scrollTop: scrollEl.scrollTop,
      scrollHeight: scrollEl.scrollHeight,
      clientHeight: scrollEl.clientHeight,
    });

    const baseTop = r.top - scrollEl.scrollTop;
    const nodes = [];

    // Doc header (icon + title) lives above the editor in the same scroll
    // container — the mini doc starts with it, like the page does
    const iconEl = scrollEl.querySelector(
      '.doc-fullpage__icon, .library-doc-pane__doc-icon'
    );
    const titleEl = scrollEl.querySelector(
      '.doc-fullpage__title-input, .library-doc-pane__title-input'
    );
    const headerNode = (el, variant) => {
      if (!el) return;
      const br = el.getBoundingClientRect();
      if (br.height <= 0) return;
      nodes.push({
        id: `__${variant}`,
        variant,
        docTop: br.top - baseTop,
        docBottom: br.bottom - baseTop,
        leftFrac: Math.max(0, (br.left - blocksRect.left) / blocksRect.width),
        widthFrac: Math.min(1, br.width / blocksRect.width),
      });
    };
    headerNode(iconEl, 'doc-icon');
    headerNode(titleEl, 'doc-title');

    blocksEl.querySelectorAll(':scope > [data-block-id]').forEach((wrapper) => {
      const node = buildNode(wrapper, blocksRect, baseTop);
      if (node) nodes.push(node);
    });

    const items = [];
    const anchors = [{ doc: 0, mini: 0 }];
    // Start below a small pad so the viewport's top edge at scroll 0 doesn't
    // slice through the icon — mirrors the real page's padding above it
    const totalH = layoutNodes(nodes, SCENE_TOP_PAD, items, anchors);
    anchors.push({ doc: scrollEl.scrollHeight, mini: totalH });
    anchors.sort((a, b) => a.doc - b.doc || a.mini - b.mini);
    // Safety net: the doc↔mini map must be monotonic in BOTH axes or
    // interpolating back and forth oscillates (viewport drag jitter)
    let maxMini = 0;
    anchors.forEach((a) => {
      if (a.mini < maxMini) a.mini = maxMini;
      else maxMini = a.mini;
    });
    setScene({ items, anchors, totalH });
  }, [editorRootRef, scrollEl, setScrollEl]);

  useEffect(() => {
    if (!scrollEl) return undefined;
    let raf = null;
    const schedule = () => {
      // A navigator drag is already scrolling the page. Rebuilding the mini
      // scene mid-gesture walks every block and clears the canvas — the rail
      // flashes and the thumb jumps. Catch up once the pointer is released.
      if (dragRef?.current?.active) {
        deferredRef.current = true;
        return;
      }
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        measure();
      });
    };
    // Coalesced variant for signals that fire per keystroke while typing
    // (content growth resizes the editor root; text mutations). A trailing
    // window keeps the minimap fresh at pauses without paying a full
    // DOM measure per character — and the measure itself waits for an idle
    // slot so it can't land in the gap right before the next keystroke
    // (on battery-throttled CPUs that collision is a visible input hitch).
    const scheduleIdle = () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(schedule, { timeout: 1000 });
      } else {
        schedule();
      }
    };
    let calmTimer = null;
    const scheduleCalm = () => {
      clearTimeout(calmTimer);
      calmTimer = setTimeout(scheduleIdle, 250);
    };
    let scrollRaf = null;
    const onScroll = () => {
      // The drag loop positions the thumb itself. Reading scrollTop back
      // through React makes the window chase the page a frame late.
      if (dragRef?.current?.active) return;
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = null;
        const scrollTop = scrollEl.scrollTop;
        // scrollHeight / clientHeight force a layout of the whole document.
        // They do not change during a scroll — reading them here was a hitch
        // every frame, worst while a tall table or section was on screen.
        setMetrics((prev) => (prev.scrollTop === scrollTop ? prev : { ...prev, scrollTop }));
      });
    };
    schedule();
    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', schedule);
    let rectRaf = null;
    const scheduleRect = () => {
      if (dragRef?.current?.active) {
        deferredRef.current = true;
        return;
      }
      if (rectRaf) return;
      rectRaf = requestAnimationFrame(() => {
        rectRaf = null;
        syncRect();
      });
    };
    const main = scrollEl.closest('.app-main');
    let followRaf = null;
    const followTick = () => {
      syncRect();
      followRaf = requestAnimationFrame(followTick);
    };
    const startFollow = (e) => {
      if (e.propertyName !== 'margin-right') return;
      if (followRaf) return;
      followTick();
    };
    const stopFollow = (e) => {
      if (e.propertyName && e.propertyName !== 'margin-right') return;
      if (followRaf) {
        cancelAnimationFrame(followRaf);
        followRaf = null;
      }
      syncRect();
    };
    main?.addEventListener('transitionrun', startFollow);
    main?.addEventListener('transitionend', stopFollow);
    main?.addEventListener('transitioncancel', stopFollow);
    // Geometry (brain open/close) must move the rail this frame. Scene
    // rebuilds stay calm so typing doesn't pay a full Range walk.
    const ro = new ResizeObserver(() => {
      scheduleRect();
      scheduleCalm();
    });
    ro.observe(scrollEl);
    const root = editorRootRef.current;
    let mo = null;
    let tickMeasureTimer = null;
    let typeMeasureTimer = null;
    if (root) {
      ro.observe(root);
      mo = new MutationObserver((mutations) => {
        const tickOnly = mutations.every(
          (m) =>
            m.type === 'attributes' &&
            m.attributeName === 'class' &&
            (m.target.classList?.contains('checkbox-content') ||
              m.target.classList?.contains('checkbox-toggle'))
        );
        if (tickOnly) {
          clearTimeout(tickMeasureTimer);
          tickMeasureTimer = setTimeout(schedule, 80);
          return;
        }
        // Typing mutates characterData (and react-contenteditable swaps
        // text nodes) on EVERY keystroke — a full remeasure per character
        // (Range.getClientRects over every block) makes typing feel laggy.
        // Structural changes (blocks added/removed) still measure promptly;
        // pure text edits coalesce until a short pause.
        const textOnly = mutations.every(
          (m) =>
            m.type === 'characterData' ||
            (m.target.nodeType === 1 && m.target.closest?.('.doc-editable'))
        );
        if (textOnly) {
          clearTimeout(typeMeasureTimer);
          typeMeasureTimer = setTimeout(scheduleIdle, 250);
        } else {
          // Structural, but can still be keystroke-driven (e.g. the debounced
          // commit re-rendering a block) — coalesce briefly rather than
          // measuring inline with input
          scheduleCalm();
        }
      });
      mo.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        // class changes cover checkbox ticks (`.checked` on the content)
        // and callout color swaps; style / data-var-state cover pill colours
        attributes: true,
        // data-code-rev covers code block edits (a textarea's value never
        // mutates the DOM, so typing would otherwise be invisible here)
        attributeFilter: ['class', 'style', 'data-code-rev', 'data-var-state'],
      });
    }
    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', schedule);
      main?.removeEventListener('transitionrun', startFollow);
      main?.removeEventListener('transitionend', stopFollow);
      main?.removeEventListener('transitioncancel', stopFollow);
      ro.disconnect();
      mo?.disconnect();
      if (tickMeasureTimer) clearTimeout(tickMeasureTimer);
      if (typeMeasureTimer) clearTimeout(typeMeasureTimer);
      if (calmTimer) clearTimeout(calmTimer);
      if (raf) cancelAnimationFrame(raf);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      if (rectRaf) cancelAnimationFrame(rectRaf);
      if (followRaf) cancelAnimationFrame(followRaf);
    };
  }, [scrollEl, measure, editorRootRef, syncRect, dragRef]);

  const finishDrag = useCallback(() => {
    if (!scrollEl?.isConnected) return;
    setMetrics({
      scrollTop: scrollEl.scrollTop,
      scrollHeight: scrollEl.scrollHeight,
      clientHeight: scrollEl.clientHeight,
    });
    if (!deferredRef.current) return;
    deferredRef.current = false;
    requestAnimationFrame(() => measure());
  }, [scrollEl, measure]);

  return { rect, scene, metrics, finishDrag };
};
